import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { WorkingStepsController } from 'controllers/WorkingStepsController'
import { RowThumbnailButton } from 'features/BillOfMaterials/components/BomItemThumbnailButton'
import { fetchProject } from 'features/BillOfMaterials/store/asyncActions/fetchProject'
import { bomItemSelector } from 'features/BillOfMaterials/store/selectors/bomItemSelector'
import { BomItemType, PartTypeRow } from 'model/Project/BoMItemRow'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BoMItemActivityDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import {
  RootAppState,
  useAppDispatch,
  useAppSelector,
} from 'store/configureStore'

/** Activity is copyable if it's not raw material, or if it's powder coating */
const isCopyableActivity = (a: BoMItemActivityDto) =>
  isCopyableActivity(a) ||
  a.primaryWorkingStep === WorkingStepType.PowderCoating

export type CopyWorkingStepsDialogProps = {
  selectedPointers: BomItemPointer[]
  onClose?: () => void
}

type Props = CopyWorkingStepsDialogProps & {
  open: boolean
  onClose: () => void
}

export const CopyWorkingStepsDialog = ({
  selectedPointers,
  open,
  onClose,
}: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const getLocalizedWorkingStep = useLocalizedWorkingStepLabel()

  const [sourcePointerId, setSourcePointerId] = useState<string>('')
  const [selectedActivityIds, setSelectedActivityIds] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  const projectId = useAppSelector(
    (state: RootAppState) => state.project.activeProject?.id,
  )

  // get bom item data for all selected pointers
  const bomItems = useAppSelector(
    (state: RootAppState) =>
      selectedPointers
        .map((p) => bomItemSelector(state, p))
        .filter(
          (item) =>
            item &&
            (item.type === BomItemType.partType ||
              item.type === BomItemType.partInstance),
        ) as PartTypeRow[],
  )

  const sourcePointer = useMemo(
    () => selectedPointers.find((p) => p.id === sourcePointerId),
    [selectedPointers, sourcePointerId],
  )

  // only items that have non-raw-material activities can be a source
  const sourceEligibleItems = useMemo(
    () =>
      bomItems.filter((item) =>
        item.activities?.some((a) => isCopyableActivity(a)),
      ),
    [bomItems],
  )

  // default to first eligible item
  useEffect(() => {
    if (!sourcePointerId && sourceEligibleItems.length > 0) {
      setSourcePointerId(sourceEligibleItems[0].id)
    }
  }, [sourcePointerId, sourceEligibleItems])

  const sourceItem = useMemo(
    () => bomItems.find((item) => item.id === sourcePointerId),
    [bomItems, sourcePointerId],
  )

  // activities from source that don't use raw material
  const availableActivities = useMemo(() => {
    if (!sourceItem?.activities) return []
    return sourceItem.activities.filter((a) => isCopyableActivity(a))
  }, [sourceItem])

  const destinationItems = useMemo(
    () => bomItems.filter((item) => item.id !== sourcePointerId),
    [bomItems, sourcePointerId],
  )

  const destinationPointers = useMemo(
    () => selectedPointers.filter((p) => p.id !== sourcePointerId),
    [selectedPointers, sourcePointerId],
  )

  const handleToggleActivity = useCallback((activityId: string) => {
    setSelectedActivityIds((prev) =>
      prev.includes(activityId)
        ? prev.filter((id) => id !== activityId)
        : [...prev, activityId],
    )
  }, [])

  const handleSourceChange = useCallback((newSourceId: string) => {
    setSourcePointerId(newSourceId)
    setSelectedActivityIds([])
  }, [])

  const handleSave = useCallback(async () => {
    if (
      !sourcePointer ||
      selectedActivityIds.length === 0 ||
      destinationPointers.length === 0
    ) {
      return
    }

    setSaving(true)
    try {
      const controller = new WorkingStepsController()
      const activitiesToCopy = availableActivities.filter((a) =>
        selectedActivityIds.includes(a.id),
      )

      await Promise.all(
        activitiesToCopy.map((activity) =>
          controller.CopyWorkingStepsToOtherParts(
            sourcePointer,
            activity.id,
            activity.primaryWorkingStep,
            destinationPointers,
          ),
        ),
      )

      dispatch(fetchProject({ projectId }))
      onClose()
    } catch (err) {
      console.error('Failed to copy working steps', err)
    } finally {
      setSaving(false)
    }
  }, [
    sourcePointer,
    selectedActivityIds,
    destinationPointers,
    availableActivities,
    dispatch,
    projectId,
    onClose,
  ])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t(
          'project:copy-working-steps-title',
          'Copy working steps to selected items',
        )}
      </DialogTitle>
      <DialogContent sx={{ height: '60vh', overflow: 'hidden', p: 0 }}>
        <Box sx={{ display: 'flex', gap: 2, height: '100%', p: 2 }}>
          {/* Left column: Source item + activities */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              {t('project:copy-working-steps-source', 'Source item')}
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Select
                value={sourcePointerId}
                label={t('project:copy-working-steps-source', 'Source item')}
                onChange={(e) => handleSourceChange(e.target.value as string)}
                renderValue={(selectedId) => {
                  const item = sourceEligibleItems.find(
                    (i) => i.id === selectedId,
                  )
                  const pointer = selectedPointers.find(
                    (p) => p.id === selectedId,
                  )
                  if (!item || !pointer) return selectedId
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption">
                        {item.rowNumber}.
                      </Typography>
                      <RowThumbnailButton
                        bomItemPointer={pointer}
                        size="small"
                        disableClick
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body1" fontWeight="bold">
                          {item.name || item.id}
                        </Typography>
                        {item.activities?.filter((a) => isCopyableActivity(a))
                          .length > 0 && (
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 0.5,
                            }}
                          >
                            {item.activities
                              .filter((a) => isCopyableActivity(a))
                              .map((a) => (
                                <WorkingStepIconSvg
                                  key={a.id}
                                  workingStepType={a.primaryWorkingStep}
                                  attributes={{
                                    width: '24px',
                                    height: '24px',
                                  }}
                                />
                              ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )
                }}
              >
                {sourceEligibleItems.map((item) => {
                  const pointer = selectedPointers.find((p) => p.id === item.id)
                  return (
                    <MenuItem key={item.id} value={item.id}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Typography variant="caption">
                          {item.rowNumber}.
                        </Typography>
                        <RowThumbnailButton
                          bomItemPointer={pointer}
                          size="small"
                          disableClick
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body1" fontWeight="bold">
                            {item.name || item.id}
                          </Typography>
                          {item.activities?.filter((a) => isCopyableActivity(a))
                            .length > 0 && (
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                              }}
                            >
                              {item.activities
                                .filter((a) => isCopyableActivity(a))
                                .map((a) => (
                                  <WorkingStepIconSvg
                                    key={a.id}
                                    workingStepType={a.primaryWorkingStep}
                                    attributes={{
                                      width: '24px',
                                      height: '24px',
                                    }}
                                  />
                                ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>

            <Typography variant="subtitle2" gutterBottom>
              {t(
                'project:copy-working-steps-select-activities',
                'Working steps to copy',
              )}
            </Typography>

            {availableActivities.length === 0 ? (
              <Box sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
                {t(
                  'project:copy-working-steps-no-activities',
                  'No working steps available to copy from this item',
                )}
              </Box>
            ) : (
              <List dense sx={{ flex: 1, overflow: 'auto' }}>
                {availableActivities.map((activity: BoMItemActivityDto) => {
                  const checked = selectedActivityIds.includes(activity.id)
                  return (
                    <ListItem key={activity.id} disablePadding>
                      <ListItemButton
                        onClick={() => handleToggleActivity(activity.id)}
                        sx={{
                          gap: 1,
                          alignItems: 'center',
                        }}
                      >
                        <Checkbox
                          edge="start"
                          checked={checked}
                          disableRipple
                        />
                        <ListItemIcon
                          sx={{ alignSelf: 'baseline', minWidth: 0 }}
                        >
                          <WorkingStepIconSvg
                            workingStepType={activity.primaryWorkingStep}
                            disableTooltip
                            attributes={{ width: 36 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={getLocalizedWorkingStep(
                            activity.primaryWorkingStep,
                          )}
                          secondary={activity.comment}
                        />
                      </ListItemButton>
                    </ListItem>
                  )
                })}
              </List>
            )}
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* Right column: Destination items */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              {t(
                'project:copy-working-steps-destinations',
                'Destination items ({{count}})',
                { count: destinationItems.length },
              )}
            </Typography>
            <List dense sx={{ flex: 1, overflow: 'auto' }}>
              {destinationItems.map((item) => {
                const pointer = selectedPointers.find((p) => p.id === item.id)
                return (
                  <ListItem
                    key={item.id}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <Typography variant="caption">{item.rowNumber}.</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RowThumbnailButton
                        bomItemPointer={pointer}
                        size="small"
                        disableClick
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body1" fontWeight="bold">
                          {item.name || item.id}
                        </Typography>
                        {item.activities.length > 0 && (
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 0.5,
                            }}
                          >
                            {item.activities.map((a) => (
                              <WorkingStepIconSvg
                                key={a.id}
                                workingStepType={a.primaryWorkingStep}
                                attributes={{
                                  width: '24px',
                                  height: '24px',
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </ListItem>
                )
              })}
            </List>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:cancel', 'Cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || selectedActivityIds.length === 0}
        >
          {saving
            ? t('common:saving', 'Saving...')
            : t(
                'project:copy-working-steps-save-count',
                'Copy to {{count}} items',
                {
                  count: destinationPointers.length,
                },
              )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
